import { atom, useAtomValue, useSetAtom } from 'jotai';
import Lenis from 'lenis';
import { useEffect, useState, useRef } from 'react';

const lenisAtom = atom<Lenis | null>(null);

export function useLenis() {
	const lenis = useAtomValue(lenisAtom);
	return lenis;
}

export function useSaveLenis() {
	const setLenis = useSetAtom(lenisAtom);

	return (newLenis: Lenis | null) => {
		setLenis(newLenis);
	};
}

type ScrollableContainerProps = {
	scrollDirection?: 'vertical' | 'horizontal';
	children: React.ReactNode;
};

const wrapperID = `lenis-scroll-wrapper`;
const contentID = `lenis-scroll-content`;

export function ScrollableContainer(props: ScrollableContainerProps) {
	const { children, scrollDirection = 'vertical' } = props;
	const [isTouchScreen, setIsTouchScreen] = useState<boolean>(false);

	const saveLenis = useSaveLenis();

	useEffect(() => {
		const isTouchScreen = 'ontouchstart' in window;
		setIsTouchScreen(isTouchScreen);

		let wrapper: HTMLElement | undefined = undefined;
		let content: HTMLElement | undefined = undefined;

		const wrapperElement = getScrollWrapper();
		wrapper = wrapperElement ? wrapperElement : undefined;

		const contentElement = getScrollContent();
		content = contentElement ? contentElement : undefined;

		lenisSetup(isTouchScreen, scrollDirection, saveLenis, wrapper, content);
	}, [saveLenis, scrollDirection]);

	const wrapperStyles = isTouchScreen
		? scrollDirection === 'vertical'
			? scrollWrapperVerticalTouchStyles
			: scrollWrapperHorizontalTouchStyles
		: scrollWrapperStyles;
	const contentStyles = isTouchScreen
		? scrollDirection === 'vertical'
			? scrollContentVerticalTouchStyles
			: scrollContentHorizontalTouchStyles
		: scrollDirection === 'vertical'
			? scrollContentVerticalDesktopStyles
			: scrollContentHorizontalDesktopStyles;

	return (
		<div id={wrapperID} style={wrapperStyles}>
			<div id={contentID} style={contentStyles}>
				<div id='top' />
				{children}
			</div>
		</div>
	);
}

export function lenisSetup(isTouchScreen, scrollDirection, saveLenis, wrapper, content) {
	if (isTouchScreen) {
		const lenis = new Lenis({
			wrapper,
			content,
			orientation: scrollDirection,
			gestureOrientation: 'both',
			syncTouch: true,
			touchMultiplier: 25,
		});

		function raf(time) {
			lenis.raf(time);
			requestAnimationFrame(raf);
		}
		requestAnimationFrame(raf);

		// Save current lenis instance to use its scroll value in other components
		saveLenis(lenis);
	} else {
		const lenis = new Lenis({
			wrapper,
			content,
			smoothWheel: true,
			orientation: scrollDirection,
			gestureOrientation: 'both', //allows horizontal gestures on scrolling on touchpad and using mouse wheel at the same time
		});

		function raf(time) {
			lenis.raf(time);
			requestAnimationFrame(raf);
		}
		requestAnimationFrame(raf);

		// Save current lenis instance to use its scroll value in other components
		saveLenis(lenis);
	}
}

export function getScrollWrapper() {
	const wrapperElement = document.getElementById(wrapperID);

	return wrapperElement ? wrapperElement : null;
}

export function getScrollContent() {
	const contentElement = document.getElementById(contentID);

	return contentElement ? contentElement : null;
}

export const useGetPageCurrentScrollContainerRef = () => {
	const scrollWrapperRef = useRef<HTMLElement | null>(null);
	const scrollContentRef = useRef<HTMLElement | null>(null);

	useEffect(() => {
		scrollWrapperRef.current = document.getElementById(wrapperID);
		scrollContentRef.current = document.getElementById(contentID);
	}, []);

	return {
		scrollWrapperRef,
		scrollContentRef,
	};
};

export const scrollWrapperStyles = {
	width: '100vw',
	height: '100dvh',
	overflow: 'hidden',
	scrollPaddingTop: '4rem',
} as const;
const scrollWrapperVerticalTouchStyles = {
	width: '100vw',
	height: '100dvh',
	overflow: 'hidden',
	overflowY: 'auto',
	scrollPaddingTop: '4rem',
	WebkitOverflowScrolling: 'touch',
} as const;
const scrollWrapperHorizontalTouchStyles = {
	width: '100vw',
	height: '100dvh',
	overflow: 'hidden',
	overflowX: 'auto',
	scrollPaddingTop: '4rem',
	WebkitOverflowScrolling: 'touch',
} as const;

/**
 * Have height: 'max-content' is essential for regenerating scroll content height on height change for vertical scroll for horizontal scroll for desktop without touch.
 * Otherwise, height won't change, for example, on lazy images loaded after scroll height was generated the first time.
 */
export const scrollContentVerticalDesktopStyles = {
	height: 'max-content',
};
/**
 * Have width: 'max-content' is essential for regenerating scroll content width on width change for horizontal scroll for desktop without touch.
 * Otherwise, width won't change, for example, on lazy images loaded after scroll width was generated the first time.
 */
export const scrollContentHorizontalDesktopStyles = { width: 'max-content' };
export const scrollContentVerticalTouchStyles = { height: 'max-content' };
export const scrollContentHorizontalTouchStyles = { width: 'max-content' };
